import React from 'react';
import { Tag, Typography, theme } from 'antd';
import { IconUsers } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { IOperatorSearchResult } from '../../interfaces/heavyDutyMachinery/IOperator';
import { useTaxonomy } from '../../hooks/useTaxonomy';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';
import { formatCurrency } from '../../helpers/currencyHelper';

const { Title, Text } = Typography;

const MAX_VISIBLE_CATEGORIES = 3;

interface OperatorSearchCardProps {
  operator: IOperatorSearchResult;
  onClick?: () => void;
  /** Minimal photo+title+price rendering for tight spaces (the map's pin-click popup) -- same role as HeavyDutyMachineryCard's own compact prop. */
  compact?: boolean;
}

/**
 * Guest-facing map/result card for an Operator -- same visual language as
 * HeavyDutyMachineryCard (photo-shaped block, rounded 16px/12px, title+price
 * layout) so switching category tabs on /explore's mobile map doesn't feel
 * like a different app. No real photo exists for an Operator (see that
 * entity's own doc comment -- staff, not a public profile with its own
 * image) -- same rounded placeholder box Machinery/Accessory cards already
 * show when a photo is missing, just with IconUsers instead of IconPhoto,
 * so the shape stays identical across every category.
 */
const OperatorSearchCard: React.FC<OperatorSearchCardProps> = ({ operator, onClick, compact }) => {
  const { t, i18n } = useTranslation();
  const { token } = theme.useToken();
  const { resolveName: categoryLabel } = useTaxonomy();

  const categories = operator.qualifiedCategories.split(',').map((c) => c.trim()).filter(Boolean);
  const priceLabel = `${formatCurrency(operator.hourlyRate, 'EUR', i18n.language)} ${translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_PRICE_UNIT_HOUR)}`;

  const photoBox = (radius: number, iconSize: number) => (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: radius,
        background: token.colorFillTertiary,
        color: token.colorTextQuaternary,
      }}
    >
      <IconUsers size={iconSize} />
    </div>
  );

  if (compact) {
    return (
      <div onClick={onClick} style={{ cursor: onClick ? 'pointer' : 'default', width: 200 }}>
        <div style={{ position: 'relative', width: '100%', aspectRatio: '16 / 10' }}>{photoBox(12, 24)}</div>
        <div style={{ paddingTop: 8 }}>
          <Text strong style={{ display: 'block', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {operator.fullName}
          </Text>
          <Text style={{ fontSize: 13 }}>{priceLabel}</Text>
        </div>
      </div>
    );
  }

  return (
    <div onClick={onClick} style={{ cursor: onClick ? 'pointer' : 'default', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ position: 'relative', width: '100%', aspectRatio: '16 / 10' }}>{photoBox(16, 32)}</div>

      <div style={{ paddingTop: 10, flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Title level={5} style={{ margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {operator.fullName}
        </Title>

        <Text type="secondary" style={{ fontSize: 13 }}>
          {operator.machineryTitle}
        </Text>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
          {categories.slice(0, MAX_VISIBLE_CATEGORIES).map((category) => (
            <Tag key={category} style={{ margin: 0, borderRadius: 999 }}>
              {categoryLabel(category)}
            </Tag>
          ))}
          {categories.length > MAX_VISIBLE_CATEGORIES && (
            <Tag style={{ margin: 0, borderRadius: 999 }}>+{categories.length - MAX_VISIBLE_CATEGORIES}</Tag>
          )}
        </div>

        <div style={{ marginTop: 'auto', paddingTop: 8 }}>
          <Text strong style={{ fontSize: 15 }}>
            {priceLabel}
          </Text>
        </div>
      </div>
    </div>
  );
};

export default OperatorSearchCard;
